import { DurationValue } from "components/DurationPicker/types";
import { routeConstants } from "constants/routeConstants";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetLanguagesQuery } from "slice/common";
import { updateNewJobData } from "slice/jobs";
import { getErrors } from "utils/getErrors";
import { validations } from "utils/validations";
import { useEditJob } from "../useEditJob";
import { DropdownInputs, DurationInputs, JobInfo, TextInputs } from "./types";

export const useJobDetails = () => {
    const requiredDropdownFields = [
        "gender",
        "language_id",
        "ethnicity",
        // "tattoos",
        // "hair_color",
    ];
    const requiredTextInputFields = ["title", "numberOfSeats", "hair_length"]; // Example, update as needed

    const [jobInfo, setJobInfo] = useState({
        title: "",
        description: "",
        numberOfSeats: "",
    });

    const [jobInfoErrors, setJobInfoErrors] = useState<JobInfo>({
        title: "",
        description: "",
        numberOfSeats: "",
    });

    const { jobs, user } = useAppSelector((state) => state);
    const dispatch = useDispatch();

    const navigateTo = useNavigate();
    useEditJob();

    const isEdit = jobs.newJobData?.isEdit;

    const [languageData, setLanguageData] = useState([]);

    const [durations, setDurations] = useState<DurationInputs>({
        experience: {
            duration: "",
            unit: "years",
        },
        duration: {
            duration: "",
            unit: "days",
        },
        minAge: {
            duration: "",
            unit: "years",
        },
        maxAge: {
            duration: "",
            unit: "years",
        },
    });

    const [textInputs, setTextInputs] = useState({
        minHeight: "",
        maxHeight: "",
        minWeight: "",
        maxWeight: "",
        bust: "",
        waist: "",
        hips: "",
        hair_length: "",
        // genres: "",
    });

    const [textInputsErrors, setTextInputsErrors] = useState<TextInputs>({
        minHeight: "",
        maxHeight: "",
        minWeight: "",
        maxWeight: "",
        bust: "",
        waist: "",
        hips: "",
        hair_length: "",
        // genres: "",
    });

    const [dropdownInputs, setDropDownInputs] = useState<DropdownInputs>({
        // qualification: "",
        language_id: "",
        ethnicity: "",
        gender: "",
        // eye_color: "",
        // tattoos: "",
        // hair_color: "",
    });

    const [dropdownInputsErrors, setDropDownInputsErrors] =
        useState<DropdownInputs>({
            // qualification: "",
            language_id: "",
            ethnicity: "",
            gender: "",
            // eye_color: "",
            // tattoos: "",
            // hair_color: "",
        });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const handleJobDetailsStartDateChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setStartDate(value);
        setEndDate("");
        // dispatch(updateNewJobData({ ...newJobData, startDate: value }));
    };

    const handleJobDetailsEndDateChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setEndDate(value);
        // dispatch(updateNewJobData({ ...newJobData, endDate: value }));
    };

    const handleTextInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        const isRequired = requiredTextInputFields.includes(name);

        setTextInputs((prev) => ({
            ...prev,
            [name]: value,
        }));

        setTextInputsErrors((prev) => ({
            ...prev,
            [name]: validations(name, value, isRequired),
        }));
    };

    const handleDropdownChange = (name: string, value: string) => {
        const isRequired = requiredDropdownFields.includes(name);
        setDropDownInputs((prev) => ({
            ...prev,
            [name]: value,
        }));

        setDropDownInputsErrors((prev) => ({
            ...prev,
            [name]: validations(name, value, isRequired),
        }));
    };

    const { data: languagesList } = useGetLanguagesQuery({});

    const { id: job_id } = useParams();

    const handleDurationChange = (name: string, value: DurationValue) => {
        setDurations((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleJobInfoChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        const input = Number(value);
        if (["numberOfSeats"].includes(name) && input > 100) {
            return;
        }
        setJobInfo((prev) => ({
            ...prev,
            [name]: value,
        }));

        setJobInfoErrors((prev) => ({
            ...prev,
            [name]: validations(name, value),
        }));
    };

    const handleBack = () => {
        if (isEdit) {
            navigateTo(
                routeConstants.jobs.editAJob.root +
                    `${jobs.newJobData?.id}/` +
                    routeConstants.jobs.editAJob.location
            );
        } else
            navigateTo(
                routeConstants.jobs.postAJob.root +
                    routeConstants.jobs.postAJob.location
            );
    };

    const handleNext = () => {
        const { hasError: hasJobInfoError, errors: updatedJobInfoErrors } =
            getErrors(
                jobInfo,
                jobInfoErrors as unknown as Record<string, string>
            );

        setJobInfoErrors(updatedJobInfoErrors as unknown as JobInfo);

        const { hasError: hasDropdownErrors, errors: updatedDropdownErrors } =
            getErrors(
                dropdownInputs,
                dropdownInputsErrors as unknown as Record<string, string>
            );

        setDropDownInputsErrors(
            updatedDropdownErrors as unknown as DropdownInputs
        );

        if (!hasDropdownErrors && !hasJobInfoError) {
            dispatch(
                updateNewJobData({
                    ...jobs.newJobData,
                    job_details: {
                        jobInfo,
                        textInputs,
                        dropdownInputs,
                        durationInputs: durations,
                        dateInputs: {
                            start_date: startDate || "",
                            end_date: endDate || "",
                        },
                    },
                })
            );

            if (isEdit) {
                navigateTo(
                    routeConstants.jobs.editAJob.root +
                        `${jobs.newJobData?.id}/` +
                        routeConstants.jobs.editAJob.advanceDetails
                );
            } else
                navigateTo(
                    routeConstants.jobs.postAJob.root +
                        routeConstants.jobs.postAJob.advanceDetails
                );
        }
    };

    useEffect(() => {
        if (
            !jobs.newJobData?.job_purpose ||
            !jobs.newJobData.job_category ||
            !jobs.newJobData.job_location
        ) {
            if (job_id) {
                navigateTo(
                    routeConstants.jobs.editAJob.root +
                        routeConstants.jobs.postAJob.category +
                        job_id
                );
            } else {
                navigateTo(
                    routeConstants.jobs.postAJob.root +
                        routeConstants.jobs.postAJob.category
                );
            }
        }
    }, [jobs, navigateTo, job_id]);

    useEffect(() => {
        if (languagesList && languagesList.length > 0) {
            setLanguageData(
                languagesList.map((language: any) => ({
                    label: language.name,
                    value: language.id,
                }))
            );
        }
    }, [languagesList]);

    useEffect(() => {
        if (jobs.newJobData?.job_details) {
            const data = jobs.newJobData?.job_details;
            setJobInfo({
                ...data.jobInfo,
            });
            const start_date =
                jobs.newJobData.job_details.dateInputs?.start_date ?? "";
            setStartDate(start_date);
            const end_date =
                jobs.newJobData.job_details.dateInputs?.end_date ?? "";
            setEndDate(end_date);
            setTextInputs({
                ...data.textInputs,
            });

            setDurations({
                ...data.durationInputs,
            });

            setDropDownInputs({
                ...data.dropdownInputs,
            });
        }
    }, [jobs.newJobData?.job_details]);

    return {
        agencyName: user.agency?.name ?? "agency",
        purposeName: jobs.newJobData?.job_purpose?.name ?? "purpose",
        location: `${jobs.newJobData?.job_location?.city}, ${jobs.newJobData?.job_location?.country_name}`,
        jobInfo,
        jobInfoErrors,
        handleJobInfoChange,
        languageData,
        durations,
        handleDurationChange,
        textInputs,
        textInputsErrors,
        dropdownInputs,
        dropdownInputsErrors,
        handleDropdownChange,
        handleTextInputChange,
        handleBack,
        handleNext,
        startDate,
        endDate,
        handleJobDetailsStartDateChange,
        handleJobDetailsEndDateChange,
    };
};
